import { useMemo, useState } from 'react'
import { Table, Modal, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { SendOutlined } from '@ant-design/icons'
import { Button, Pagination, StatusIndicator, TableSearch } from '@/components/ui'
import { csrfToken } from '@/lib/csrf'
import { WhatsappCampaign } from '../types'

const PAGE_SIZE = 10
const safeUnix = (value?: string) => (value ? dayjs(value).unix() : 0)

type Props = {
  campaigns: WhatsappCampaign[]
  eventSlug: string
}

export const CustomCampaignsTable = ({ campaigns, eventSlug }: Props) => {
  const [searchValue, setSearchValue] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)

  const filteredCampaigns = useMemo(() => {
    const query = appliedSearch.toLowerCase()
    return campaigns.filter((c) => {
      if (!query) return true
      return (
        c.campaignName.toLowerCase().includes(query) ||
        c.metaTemplateName.toLowerCase().includes(query)
      )
    })
  }, [appliedSearch, campaigns])

  const paginated = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredCampaigns.slice(startIndex, startIndex + pageSize)
  }, [filteredCampaigns, currentPage, pageSize])

  const handleSearch = () => {
    setAppliedSearch(searchValue)
    setCurrentPage(1)
  }

  const handleReset = () => {
    setSearchValue('')
    setAppliedSearch('')
    setCurrentPage(1)
  }

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page)
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize)
      setCurrentPage(1)
    }
  }

  const columns: ColumnsType<WhatsappCampaign> = [
    {
      title: 'Campaign Name',
      dataIndex: 'campaignName',
      key: 'campaignName',
      sorter: (a, b) => a.campaignName.localeCompare(b.campaignName),
    },
    {
      title: 'WhatsApp Template',
      dataIndex: 'metaTemplateName',
      key: 'metaTemplateName',
      sorter: (a, b) => a.metaTemplateName.localeCompare(b.metaTemplateName),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => safeUnix(a.createdAt) - safeUnix(b.createdAt),
      render: (date?: string) => (date ? dayjs(date).format('YYYY-MM-DD, hh:mmA') : '-'),
    },
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      sorter: (a, b) => safeUnix(a.sentAt) - safeUnix(b.sentAt),
      render: (date?: string) => (date ? dayjs(date).format('YYYY-MM-DD, hh:mmA') : '-'),
    },
    {
      title: 'Recipients',
      dataIndex: 'recipientCount',
      key: 'recipientCount',
      sorter: (a, b) => a.recipientCount - b.recipientCount,
      align: 'center' as const,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: WhatsappCampaign['status']) => (
        <StatusIndicator
          status={status === 'sent' ? 'success' : status === 'failed' ? 'error' : 'inactive'}
          label={(status || 'draft').charAt(0).toUpperCase() + (status || 'draft').slice(1)}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center' as const,
      width: 200,
      render: (_: unknown, record: WhatsappCampaign) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="small"
            onClick={() => (window.location.href = `/events/${eventSlug}/messaging/whatsapp_blasts/${record.id}/edit`)}
          >
            Edit
          </Button>
          {(record.status === 'draft' || record.status === 'failed') && (
            <Button
              variant="primary"
              size="small"
              icon={<SendOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: 'Send WhatsApp Campaign',
                  content: `Are you sure you want to send "${record.campaignName}" to ${record.recipientCount || 'all selected'} recipient(s)? This action cannot be undone.`,
                  okText: 'Send Now',
                  okType: 'primary',
                  cancelText: 'Cancel',
                  onOk: () => {
                    const form = document.createElement('form')
                    form.method = 'POST'
                    form.action = `/events/${eventSlug}/messaging/whatsapp_blasts/custom/${record.id}/send`

                    const token = csrfToken()
                    if (token) {
                      const input = document.createElement('input')
                      input.type = 'hidden'
                      input.name = 'authenticity_token'
                      input.value = token
                      form.appendChild(input)
                    }

                    document.body.appendChild(form)
                    form.submit()
                  },
                })
              }}
            >
              Send
            </Button>
          )}
        </div>
      ),
    },
  ]

  if (campaigns.length === 0) {
    return null
  }

  return (
    <div className="p-6 bg-white border border-[#F0F0F0]">
      <div className="flex items-center justify-between mb-4">
        <TableSearch
          value={searchValue}
          onChange={setSearchValue}
          onSearch={handleSearch}
          onReset={handleReset}
          placeholder="Search campaign or template"
          showFilter={false}
        />
      </div>

      <Table
        columns={columns}
        dataSource={paginated}
        rowKey="id"
        className="ava-data-table"
        pagination={false}
        showSorterTooltip={false}
      />

      {filteredCampaigns.length > 0 && (
        <div className="mt-4">
          <Pagination
            current={currentPage}
            total={filteredCampaigns.length}
            pageSize={pageSize}
            onChange={handlePageChange}
            pageSizeOptions={["10", "20", "50"]}
          />
        </div>
      )}
    </div>
  )
}
